/**
 * UTILITAIRE UNIFIÉ DE CALCUL DE PRIX - KRILLOU
 * ===============================================
 * 
 * Cet utilitaire centralise TOUS les calculs de prix et gestion des devises
 * pour éviter les incohérences entre les écrans.
 * 
 * Usage: import { calculateUnifiedPrice } from './utils/priceCalculatorUnified'
 */

import { differenceInDays, differenceInHours, isValid } from 'date-fns';
import { formatPrice, SUPPORTED_CURRENCIES, DEFAULT_CURRENCY } from '../config/currencies';
import { 
  PRICING_CONFIG, 
  calculateServiceFee, 
  calculateTax, 
  calculateDeposit,
  roundAmount,
  getRegionalConfig 
} from '../config/pricingConfig';
import ConfigService from '../services/configService';

/**
 * FONCTION PRINCIPALE - CALCUL UNIFIÉ DE PRIX
 * ==========================================
 * 
 * @param {Object} listing - Listing avec prix et devise
 * @param {Date} startDate - Date début
 * @param {Date} endDate - Date fin  
 * @param {Object} options - Options supplémentaires
 * @returns {Object} Calcul complet standardisé
 */
export const calculateUnifiedPrice = async (listing, startDate, endDate, options = {}) => {
  console.log('💰 [PriceCalculatorUnified] Calcul pour:', {
    listing: listing?.id,
    currency: listing?.currency,
    startDate,
    endDate,
    options
  });

  try {
    // 🔄 RÉCUPÉRATION CONFIGURATION DEPUIS BDD
    const dynamicConfig = await ConfigService.getCompleteConfig();
    
    // Fusionner configuration BDD + configuration statique + options
    const config = {
      ...PRICING_CONFIG,
      SERVICE_FEE_PERCENT: dynamicConfig.financial.commission.rate_percent,
      TAX_PERCENT: dynamicConfig.financial.vat.rates_by_country.DEFAULT,
      ...options
    };
    
    const regionalConfig = getDynamicRegionalConfig(listing?.country_code, dynamicConfig);

    console.log('✅ [PriceCalculatorUnified] Configuration chargée depuis BDD:', {
      commission_rate: config.SERVICE_FEE_PERCENT,
      default_tax_rate: config.TAX_PERCENT,
      country_tax_rate: regionalConfig?.TAX_PERCENT
    });

    // 1. VALIDATION DES PARAMÈTRES
    const validation = validateInputs(listing, startDate, endDate, config);
    if (!validation.isValid) {
      return {
        isValid: false,
        error: validation.error,
        currency: listing?.currency || DEFAULT_CURRENCY,
        breakdown: {},
        display: {},
        raw: {}
      };
    }

    // 2. CALCUL DE LA DURÉE
    const duration = calculateDuration(startDate, endDate, listing);
    
    // 3. EXTRACTION PRIX ET DEVISE DU LISTING
    const listingCurrency = listing.currency || DEFAULT_CURRENCY;
    const prices = extractListingPrices(listing);
    
    // 4. CALCUL DU MONTANT DE BASE (LOCATION)
    const baseCalculation = calculateBaseAmount(duration, prices);
    if (!baseCalculation.isValid) {
      return {
        isValid: false,
        error: baseCalculation.error,
        currency: listingCurrency,
        breakdown: {},
        display: {},
        raw: {}
      };
    }

    // 5. CALCUL DES FRAIS ET TAXES AVEC CONFIG DEPUIS BDD
    const feesCalculation = calculateFeesAndTaxes(baseCalculation.amount, listing, config, regionalConfig);
    
    // 6. ASSEMBLAGE FINAL
    const totalCalculation = assembleFinalCalculation({
      listing,
      duration,
      baseCalculation,
      feesCalculation,
      currency: listingCurrency,
      config
    });

    console.log('✅ [PriceCalculatorUnified] Calcul terminé:', totalCalculation);
    
    return totalCalculation;

  } catch (error) {
    console.error('❌ [PriceCalculatorUnified] Erreur lors du calcul:', error);
    
    // Fallback avec configuration statique en cas d'erreur
    console.warn('🔄 [PriceCalculatorUnified] Fallback vers configuration statique');
    return calculateUnifiedPriceFallback(listing, startDate, endDate, options);
  }
};

/**
 * VALIDATION DES PARAMÈTRES D'ENTRÉE
 */
function validateInputs(listing, startDate, endDate, config = PRICING_CONFIG) {
  if (!listing) {
    return { isValid: false, error: 'Listing manquant' };
  }

  if (!startDate || !endDate || !isValid(startDate) || !isValid(endDate)) {
    return { isValid: false, error: 'Dates invalides' };
  }

  if (startDate >= endDate) {
    return { isValid: false, error: 'Date de fin doit être après date de début' };
  }

  const dateRangeMode = listing?.booking_settings?.date_range_mode || 'days';
  // End-exclusive for both modes: 22→23 = 1 jour/nuit
  const daysDifference = differenceInDays(endDate, startDate);
  if (daysDifference < config.MIN_RENTAL_DAYS) {
    return { isValid: false, error: `Durée minimum ${config.MIN_RENTAL_DAYS} jour${config.MIN_RENTAL_DAYS > 1 ? 's' : ''}` };
  }

  if (daysDifference > config.MAX_RENTAL_DAYS) {
    return { isValid: false, error: `Durée maximum ${config.MAX_RENTAL_DAYS} jours` };
  }

  return { isValid: true };
}

/**
 * CALCUL DE LA DURÉE
 * - days: end-exclusive (22→23 = 1 jour) → location matériel
 * - nights: end-exclusive (22→23 = 1 nuit) → hébergement
 */
function calculateDuration(startDate, endDate, listing = null) {
  const unit = listing?.booking_settings?.unit || 'day';
  const dateRangeMode = listing?.booking_settings?.date_range_mode || 'days';
  const slotDuration = parseInt(listing?.booking_settings?.slot_duration, 10) || 60;
  const bufferBefore = parseInt(listing?.booking_settings?.buffer_before, 10) || 0;
  const bufferAfter = parseInt(listing?.booking_settings?.buffer_after, 10) || 0;

  // End-exclusive pour les deux modes (nuitées et journées) :
  // 22→23 = 1 jour/nuit, la date de fin est le premier jour libre
  const totalDays = differenceInDays(endDate, startDate);
  
  // Calcul de la durée en minutes pour les modes horaires
  const durationMs = endDate.getTime() - startDate.getTime();
  const durationMinutes = Math.round(durationMs / (1000 * 60));
  const totalHours = durationMinutes / 60;
  
  // Calcul du nombre de slots
  const slotStep = slotDuration + bufferBefore + bufferAfter;
  const totalSlots = slotStep > 0 && durationMinutes >= slotDuration
    ? 1 + Math.floor((durationMinutes - slotDuration) / slotStep)
    : 1;

  const totalWeeks = Math.floor(totalDays / 7);
  const remainingDays = totalDays % 7;

  let displayText;
  let effectiveDuration;
  if (unit === 'hour') {
    effectiveDuration = totalHours;
    displayText = `${totalHours.toFixed(1)} heure${totalHours > 1 ? 's' : ''}`;
  } else if (unit === 'slot') {
    effectiveDuration = totalSlots;
    displayText = `${totalSlots} créneau${totalSlots > 1 ? 'x' : ''} (${slotDuration}min)`;
  } else {
    effectiveDuration = totalDays;
    if (dateRangeMode === 'nights') {
      displayText = `${totalDays} nuitée${totalDays > 1 ? 's' : ''}`;
    } else {
      displayText = `${totalDays} jour${totalDays > 1 ? 's' : ''}`;
    }
  }

  return {
    totalHours,
    totalDays,
    totalSlots,
    totalWeeks,
    remainingDays,
    unit,
    dateRangeMode,
    effectiveDuration,
    slotDuration,
    durationMinutes,
    displayText
  };
}

/**
 * EXTRACTION DES PRIX DU LISTING
 */
function extractListingPrices(listing) {
  return {
    hourly: parseFloat(listing.price_hourly) || 0,
    daily: parseFloat(listing.price_daily) || 0,
    weekly: parseFloat(listing.price_weekly) || 0,
    monthly: parseFloat(listing.price_monthly) || 0,
    deposit: parseFloat(listing.deposit_amount) || 0
  };
}

/**
 * CALCUL DU MONTANT DE BASE (LOCATION)
 */
function calculateBaseAmount(duration, prices) {
  const { totalDays, unit, effectiveDuration, totalSlots, totalHours } = duration;
  
  // FIX9: Support unit=hour et unit=slot
  if (unit === 'hour') {
    if (prices.hourly <= 0) {
      return { isValid: false, error: 'Tarif horaire non disponible' };
    }
    const amount = totalHours * prices.hourly;
    return {
      isValid: true,
      amount: Math.round(amount * 100) / 100,
      method: 'hourly',
      description: `${totalHours.toFixed(1)} heure${totalHours > 1 ? 's' : ''} \u00d7 ${prices.hourly}\u20ac/heure`
    };
  }

  if (unit === 'slot') {
    // Pour les créneaux, utiliser price_hourly ou le base_price comme tarif par slot
    const slotPrice = prices.hourly || prices.daily || 0;
    if (slotPrice <= 0) {
      return { isValid: false, error: 'Tarif par créneau non disponible' };
    }
    const amount = totalSlots * slotPrice;
    return {
      isValid: true,
      amount: Math.round(amount * 100) / 100,
      method: 'slot',
      description: `${totalSlots} créneau${totalSlots > 1 ? 'x' : ''} \u00d7 ${slotPrice}\u20ac/créneau`
    };
  }

  // Mode day (défaut) — logique existante
  let amount = 0;
  let method = '';
  let description = '';

  if (totalDays < 7) {
    // Moins d'une semaine = tarif journalier
    if (prices.daily <= 0) {
      return { isValid: false, error: 'Tarif journalier non disponible' };
    }
    amount = totalDays * prices.daily;
    method = 'daily';
    description = `${totalDays} jour${totalDays > 1 ? 's' : ''} × ${prices.daily}€/jour`;
    
  } else if (totalDays >= 7 && totalDays < 30) {
    // Une semaine ou plus = tarif hebdomadaire si disponible
    if (prices.weekly > 0) {
      const weeks = Math.floor(totalDays / 7);
      const remainingDays = totalDays % 7;
      
      amount = weeks * prices.weekly;
      method = 'weekly';
      description = `${weeks} semaine${weeks > 1 ? 's' : ''} × ${prices.weekly}€/semaine`;
      
      if (remainingDays > 0 && prices.daily > 0) {
        amount += remainingDays * prices.daily;
        description += ` + ${remainingDays} jour${remainingDays > 1 ? 's' : ''} × ${prices.daily}€/jour`;
      }
    } else if (prices.daily > 0) {
      // Fallback sur tarif journalier
      amount = totalDays * prices.daily;
      method = 'daily_fallback';
      description = `${totalDays} jours × ${prices.daily}€/jour`;
    } else {
      return { isValid: false, error: 'Aucun tarif disponible' };
    }
    
  } else {
    // Un mois ou plus = tarif mensuel si disponible
    if (prices.monthly > 0) {
      const months = Math.floor(totalDays / 30);
      const remainingDays = totalDays % 30;
      
      amount = months * prices.monthly;
      method = 'monthly';
      description = `${months} mois × ${prices.monthly}€/mois`;
      
      if (remainingDays > 0) {
        if (remainingDays >= 7 && prices.weekly > 0) {
          const weeks = Math.floor(remainingDays / 7);
          const finalDays = remainingDays % 7;
          
          amount += weeks * prices.weekly;
          description += ` + ${weeks} semaine${weeks > 1 ? 's' : ''}`;
          
          if (finalDays > 0 && prices.daily > 0) {
            amount += finalDays * prices.daily;
            description += ` + ${finalDays} jour${finalDays > 1 ? 's' : ''}`;
          }
        } else if (prices.daily > 0) {
          amount += remainingDays * prices.daily;
          description += ` + ${remainingDays} jour${remainingDays > 1 ? 's' : ''}`;
        }
      }
    } else {
      return { isValid: false, error: 'Tarif mensuel non disponible pour cette durée' };
    }
  }

  return {
    isValid: true,
    amount: Math.round(amount * 100) / 100, // Arrondi à 2 décimales
    method,
    description
  };
}

/**
 * CALCUL DES FRAIS ET TAXES - VERSION CORRIGÉE
 * ✅ LOGIQUE CORRECTE : Client paie prix TTC, commission/TVA déduites en interne
 */
function calculateFeesAndTaxes(baseAmount, listing, config = PRICING_CONFIG, regionalConfig = null) {
  console.log('💰 [calculateFeesAndTaxes] Calcul avec logique CORRECTE TTC:', {
    baseAmount,
    config: {
      SERVICE_FEE_PERCENT: config.SERVICE_FEE_PERCENT,
      TAX_PERCENT: regionalConfig?.TAX_PERCENT || config.TAX_PERCENT,
      TAX_APPLIED_ON: config.TAX_APPLIED_ON
    }
  });

  // ✅ NOUVELLE LOGIQUE : Client paie prix affiché (TTC)
  const clientPays = baseAmount; // Prix affiché que paie le client
  
  // Commission et TVA déduites en interne
  const commissionAmount = clientPays * (config.SERVICE_FEE_PERCENT / 100);
  const taxAmount = commissionAmount * ((regionalConfig?.TAX_PERCENT || config.TAX_PERCENT) / 100);
  const rentalAmount = clientPays - commissionAmount - taxAmount; // Ce qui reste pour le propriétaire
  const ownerReceives = rentalAmount; // ✅ CORRECTION: Identique à rentalAmount (TVA payée par Krillou)
  
  const depositAmount = calculateDeposit(listing, baseAmount, config);
  
  return {
    // ✅ MONTANTS CORRECTS
    clientPays: roundAmount(clientPays, config),          // Ce que paie le client
    rentalAmount: roundAmount(rentalAmount, config),      // Payment RENTAL
    commissionAmount: roundAmount(commissionAmount, config), // Payment COMMISSION
    taxAmount: roundAmount(taxAmount, config),            // Payment TAX
    ownerReceives: roundAmount(ownerReceives, config),    // Net propriétaire
    depositAmount: roundAmount(depositAmount, config),
    
    // Pour compatibilité (transition progressive)
    serviceFee: roundAmount(commissionAmount, config),
    serviceFeePercent: (commissionAmount / clientPays) * 100,
    taxPercent: regionalConfig?.TAX_PERCENT || config.TAX_PERCENT,
    taxName: regionalConfig?.TAX_NAME || 'TVA',
    
    // Infos additionnelles pour debug
    calculation_method: 'corrected_ttc_logic'
  };
}

/**
 * ASSEMBLAGE DU CALCUL FINAL - VERSION CORRIGÉE
 */
function assembleFinalCalculation({ listing, duration, baseCalculation, feesCalculation, currency, config }) {
  // ✅ NOUVELLE LOGIQUE : Client paie le prix affiché (TTC)
  const clientTotal = feesCalculation.clientPays;
  const totalWithDeposit = clientTotal + feesCalculation.depositAmount;
  
  return {
    isValid: true,
    currency: currency,
    
    // DONNÉES BRUTES (pour backend/API)
    raw: {
      client_total: clientTotal,                    // ✅ Ce que paie le client
      rental_amount: feesCalculation.rentalAmount,  // Payment RENTAL
      commission_amount: feesCalculation.commissionAmount, // Payment COMMISSION
      tax_amount: feesCalculation.taxAmount,        // Payment TAX
      owner_net_amount: feesCalculation.ownerReceives, // Net propriétaire
      deposit_amount: feesCalculation.depositAmount,
      total_with_deposit: totalWithDeposit,
      days: duration.totalDays,
      
      // Taux utilisés (pour affichage)
      commission_rate_used: config.SERVICE_FEE_PERCENT,
      vat_rate_used: feesCalculation.taxPercent,
      
      // Pour compatibilité transitoire
      service_fee: feesCalculation.commissionAmount,
      total_amount: clientTotal // ✅ Prix TTC réel
    },
    
    // AFFICHAGE UTILISATEUR (formaté avec devise)
    display: {
      clientPays: formatPrice(clientTotal, currency),
      ownerReceives: formatPrice(feesCalculation.ownerReceives, currency),
      commission: formatPrice(feesCalculation.commissionAmount, currency),
      tax: formatPrice(feesCalculation.taxAmount, currency),
      deposit: formatPrice(feesCalculation.depositAmount, currency),
      totalWithDeposit: formatPrice(totalWithDeposit, currency),
      duration: duration.displayText,
      method: baseCalculation.description,
      
      // Pour compatibilité
      rental: formatPrice(feesCalculation.rentalAmount, currency),
      serviceFee: formatPrice(feesCalculation.commissionAmount, currency),
      totalWithoutDeposit: formatPrice(clientTotal, currency)
    },
    
    // DÉTAIL POUR STRIPE/BACKEND (montants en centimes si nécessaire)
    breakdown: {
      client_total: clientTotal,
      rental_amount: feesCalculation.rentalAmount,
      deposit_amount: feesCalculation.depositAmount,
      service_fee: feesCalculation.serviceFee,
      total_amount: totalWithDeposit, // TOTAL AVEC CAUTION pour Stripe
      currency: currency.toLowerCase(),
      days: duration.totalDays,
      
      // Lignes d'affichage pour récapitulatifs - LOGIQUE CORRIGÉE
      lines: [
        {
          label: `Total location (${duration.displayText})`,
          amount: clientTotal, // ✅ Client paie ce montant TTC
          formatted: formatPrice(clientTotal, currency),
          isMainAmount: true
        },
        {
          label: `Dont commission Krillou (${feesCalculation.serviceFeePercent.toFixed(1)}%)`,
          amount: feesCalculation.commissionAmount,
          formatted: formatPrice(feesCalculation.commissionAmount, currency),
          isInternal: true,
          note: 'Déduit en interne'
        },
        {
          label: `Dont TVA (${feesCalculation.taxPercent}%)`,
          amount: feesCalculation.taxAmount,
          formatted: formatPrice(feesCalculation.taxAmount, currency),
          isInternal: true,
          note: 'Payée par Krillou'
        },
        {
          label: `Net propriétaire`,
          amount: feesCalculation.ownerReceives,
          formatted: formatPrice(feesCalculation.ownerReceives, currency),
          isOwnerAmount: true
        },
        ...(feesCalculation.depositAmount > 0 ? [{
          label: 'Caution (remboursable)',
          amount: feesCalculation.depositAmount,
          formatted: formatPrice(feesCalculation.depositAmount, currency),
          isDeposit: true
        }] : []),
        {
          label: 'Total à payer',
          amount: totalWithDeposit,
          formatted: formatPrice(totalWithDeposit, currency),
          isTotal: true
        }
      ]
    },
    
    // MÉTADONNÉES
    meta: {
      calculatedAt: new Date().toISOString(),
      listingId: listing.id,
      method: baseCalculation.method,
      description: baseCalculation.description
    }
  };
}

/**
 * UTILITAIRE POUR FORMATER UN PRIX SIMPLE
 */
export const formatUnifiedPrice = (amount, currency = DEFAULT_CURRENCY) => {
  return formatPrice(amount, currency);
};

/**
 * UTILITAIRE POUR OBTENIR LA DEVISE D'UN LISTING
 */
export const getListingCurrency = (listing) => {
  const currency = listing?.currency || DEFAULT_CURRENCY;
  
  // Vérifier que la devise est supportée
  if (SUPPORTED_CURRENCIES[currency]) {
    return currency;
  }
  
  console.warn(`[PriceCalculatorUnified] Devise non supportée: ${currency}, utilisation de ${DEFAULT_CURRENCY}`);
  return DEFAULT_CURRENCY;
};

/**
 * VALIDATION D'UNE DEVISE
 */
export const isSupportedCurrency = (currency) => {
  return SUPPORTED_CURRENCIES.hasOwnProperty(currency);
};

/**
 * Récupère la configuration régionale depuis les données BDD
 * @param {string} countryCode - Code pays
 * @param {Object} dynamicConfig - Configuration depuis BDD
 * @returns {Object} Configuration régionale
 */
function getDynamicRegionalConfig(countryCode = 'FR', dynamicConfig) {
  const vatRates = dynamicConfig.financial.vat.rates_by_country;
  const taxRate = vatRates[countryCode] || vatRates.DEFAULT || 20;
  
  return {
    TAX_PERCENT: taxRate,
    TAX_NAME: 'TVA',
    CURRENCY: getCountryCurrency(countryCode)
  };
}

/**
 * Récupère la devise par défaut pour un pays
 * @param {string} countryCode - Code pays
 * @returns {string} Code devise
 */
function getCountryCurrency(countryCode) {
  const currencyMap = {
    'FR': 'EUR',
    'DZ': 'DZD', 
    'MA': 'MAD',
    'TN': 'TND'
  };
  return currencyMap[countryCode] || 'EUR';
}

/**
 * Fonction de fallback si la BDD n'est pas disponible
 * @param {Object} listing - Listing
 * @param {Date} startDate - Date début
 * @param {Date} endDate - Date fin
 * @param {Object} options - Options
 * @returns {Object} Calcul avec configuration statique
 */
function calculateUnifiedPriceFallback(listing, startDate, endDate, options = {}) {
  console.warn('⚠️ [PriceCalculatorUnified] Mode fallback - configuration statique');
  
  // Fusionner la configuration avec les options
  const config = { ...PRICING_CONFIG, ...options };
  const regionalConfig = getRegionalConfig(listing?.country_code);

  // 1. VALIDATION DES PARAMÈTRES
  const validation = validateInputs(listing, startDate, endDate, config);
  if (!validation.isValid) {
    return {
      isValid: false,
      error: validation.error,
      currency: listing?.currency || DEFAULT_CURRENCY,
      breakdown: {},
      display: {},
      raw: {},
      fallback: true
    };
  }

  // 2. CALCUL DE LA DURÉE
  const duration = calculateDuration(startDate, endDate, listing);
  
  // 3. EXTRACTION PRIX ET DEVISE DU LISTING
  const listingCurrency = listing.currency || DEFAULT_CURRENCY;
  const prices = extractListingPrices(listing);
  
  // 4. CALCUL DU MONTANT DE BASE (LOCATION)
  const baseCalculation = calculateBaseAmount(duration, prices);
  if (!baseCalculation.isValid) {
    return {
      isValid: false,
      error: baseCalculation.error,
      currency: listingCurrency,
      breakdown: {},
      display: {},
      raw: {},
      fallback: true
    };
  }

  // 5. CALCUL DES FRAIS ET TAXES AVEC CONFIG STATIQUE
  const feesCalculation = calculateFeesAndTaxes(baseCalculation.amount, listing, config, regionalConfig);
  
  // 6. ASSEMBLAGE FINAL
  const totalCalculation = assembleFinalCalculation({
    listing,
    duration,
    baseCalculation,
    feesCalculation,
    currency: listingCurrency,
    config
  });

  // Marquer comme fallback
  totalCalculation.fallback = true;
  
  return totalCalculation;
}

/**
 * FONCTION DE COMPATIBILITÉ - équivalent à l'ancien calculateLocalPrice
 * Retourne la structure de données attendue par les anciens composants
 */
export const calculateLocalPrice = async (listing, startDate, endDate, options = {}) => {
  const unified = await calculateUnifiedPrice(listing, startDate, endDate, options);
  
  if (!unified.isValid) {
    return {
      isValid: false,
      error: unified.error,
      total: 0,
      breakdown: {}
    };
  }

  // Structure compatible avec l'ancien format
  return {
    isValid: true,
    method: unified.breakdown.method,
    currency: unified.currency,
    
    duration: {
      totalDays: unified.breakdown.days
    },
    
    pricing: {
      subtotal: unified.raw.rental_amount,
      serviceFee: unified.raw.service_fee,
      serviceFeePercent: options.serviceFeePercent || unified.raw.commission_rate_used || 8,
      deposit: unified.raw.deposit_amount,
      total: unified.raw.total_amount,
      // Alias pour compatibilité
      rental: unified.raw.rental_amount,
      caution: unified.raw.deposit_amount,
      service_fee: unified.raw.service_fee,
      total_amount: unified.raw.total_amount
    },
    
    breakdown: {
      method: unified.breakdown.method,
      description: unified.meta.description
    },
    
    fallback: unified.fallback || false
  };
};

/**
 * RÉCUPÈRE LES TAUX ACTUELS DEPUIS LA BDD
 * ========================================
 * 
 * Fonction utilitaire pour récupérer juste les taux
 * (utile pour affichage dans les composants)
 * 
 * @param {string} countryCode - Code pays pour la TVA
 * @returns {Promise<Object>} Taux commission et TVA
 */
export const getCurrentRates = async (countryCode = 'FR') => {
  try {
    const config = await ConfigService.getFinancialConfig();
    
    const commissionRate = config.commission.rate_percent;
    const vatRate = config.vat.rates_by_country[countryCode] || config.vat.rates_by_country.DEFAULT;
    
    return {
      commission_percent: commissionRate,
      vat_percent: vatRate,
      country_code: countryCode,
      source: 'database',
      retrieved_at: new Date().toISOString()
    };
  } catch (error) {
    console.error('❌ [getCurrentRates] Failed to fetch rates from database:', error);
    
    // Fallback
    const fallbackRates = {
      'FR': 20,
      'DZ': 19,
      'MA': 20,
      'TN': 19
    };
    
    return {
      commission_percent: 8,
      vat_percent: fallbackRates[countryCode] || 20,
      country_code: countryCode,
      source: 'fallback',
      retrieved_at: new Date().toISOString()
    };
  }
};

/**
 * VIDE LE CACHE DE CONFIGURATION
 * ===============================
 * 
 * Utile pour forcer un rechargement des taux après modification en admin
 */
export const clearConfigCache = () => {
  ConfigService.clearCache();
  console.log('🗑️ [PriceCalculatorUnified] Cache de configuration vidé');
};

export default {
  calculateUnifiedPrice,
  calculateLocalPrice,
  formatUnifiedPrice,
  getListingCurrency,
  isSupportedCurrency,
  PRICING_CONFIG
};
